import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import addSupported from '@/assets/icons/add-supported.svg';
import CheckExistence from './CheckExistence';
import { SupportedFullInfoDtoIn } from '@/models/in/SupportedFullInfoDtoIn';
import { ContactDtoIn } from '@/models/in/ContactDtoIn';
import SupportedSuccessScreen from './SupportedSuccessScreen';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import NewSupportedForm from './NewSupportedForm';
import { SupportRequestDraftDtoIn } from '@/models/in/SupportRequestDraftDtoIn';
import { mapSupportedDraftToFormData } from './utils/mapSupportedDraftToFormData';

interface NewSupportedProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onOpenSupportedAdditionalInfo?: (supportedId: number) => Promise<void>;
  onRefreshList?: () => void;
  /**
   * A supported whose details were typed in the Nedarim Plus personal area, where the
   * certificates the record needs cannot be attached, and saved to be finished here.
   *
   * ⚠️ A draft skips the id-existence step. That step was already answered where the draft was
   * written - in front of the supported, who is not in the room now - and asking again would
   * make continuing a draft slower than retyping it.
   */
  draft?: SupportRequestDraftDtoIn | null;
  /** Called once the draft's supported has been created, so the draft row can be removed. */
  onDraftCompleted?: (draft: SupportRequestDraftDtoIn) => void;
  /**
   * HAT-1453: set when the dialog was opened from a new support request. After a supported that
   * can already be requested for is saved, the success screen offers to carry straight on with it.
   * Left out (the supporteds screen), the success screen is unchanged.
   */
  onContinueToRequest?: (supported: ContactDtoIn) => void;
  /**
   * True when this dialog was opened from inside a new support request: the success screen then
   * asks "continue to the request?" in place of the usual pair. False (the supporteds screen) keeps
   * the pair and adds the extra actions below it.
   */
  startedFromRequest?: boolean;
  /** Supporteds screen: open the add-classifications dialog for the supported just created. */
  onAddClassifications?: (supported: ContactDtoIn) => void;
}

const NewSupported = ({
  open,
  setOpen,
  onOpenSupportedAdditionalInfo,
  onRefreshList,
  draft,
  onDraftCompleted,
  onContinueToRequest,
  startedFromRequest = false,
  onAddClassifications,
}: NewSupportedProps) => {
  const { t } = useTranslation();
  const [isStartSteps, setIsStartSteps] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [initialIsraeliId, setInitialIsraeliId] = useState<string | undefined>(undefined);
  const [existingSupported, setExistingSupported] = useState<SupportedFullInfoDtoIn | null>(null);
  /* The supported just created, kept for the "continue to the request" offer. */
  const [created, setCreated] = useState<{ contact: ContactDtoIn; canRequest: boolean } | null>(null);

  /*
   * Recomputed only when the draft itself changes: this is the object the form resets from, so
   * a fresh one on every render would wipe what the coordinator has typed since.
   */
  const draftValues = useMemo(() => mapSupportedDraftToFormData(draft), [draft]);

  const handleStart = (existingSupportedData: SupportedFullInfoDtoIn | null, israeliId: string) => {
    setExistingSupported(existingSupportedData);
    setInitialIsraeliId(israeliId);
    setIsStartSteps(true);
    setIsSuccess(false);
  };

  const handleBackToSupporteds = () => {
    setOpen(false);
    setIsStartSteps(false);
    setIsSuccess(false);
    setInitialIsraeliId(undefined);
    setExistingSupported(null);
    setCreated(null);
    if (onRefreshList) {
      onRefreshList();
    }
  };

  const handleAddAnother = () => {
    setIsSuccess(false);
    setIsStartSteps(false);
    setInitialIsraeliId(undefined);
    setExistingSupported(null);
    setCreated(null);
  };

  const handleContinueToRequest = () => {
    const supported = created?.contact;
    handleAddAnother();
    setOpen(false);
    if (onRefreshList) onRefreshList();
    if (supported) onContinueToRequest?.(supported);
  };

  const handleAddClassifications = () => {
    const supported = created?.contact;
    handleAddAnother();
    setOpen(false);
    if (onRefreshList) onRefreshList();
    if (supported) onAddClassifications?.(supported);
  };

  const handleDialogToggle = (value: boolean) => {
    if (!value && isSuccess && onRefreshList) {
      onRefreshList();
    }
    setOpen(value);
    setIsStartSteps(false);
    setIsSuccess(false);
    setInitialIsraeliId(undefined);
    setExistingSupported(null);
    setCreated(null);
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={handleDialogToggle}
      title={t('newSupported')}
      titleIconSrc={addSupported}>
      {isSuccess ? (
        <SupportedSuccessScreen
          onBackToSupporteds={handleBackToSupporteds}
          onAddAnother={handleAddAnother}
          continueToRequest={
            created?.canRequest && onContinueToRequest && startedFromRequest
              ? {
                  supportedName: `${created.contact.firstName ?? ''} ${created.contact.lastName ?? ''}`.trim(),
                  onContinue: handleContinueToRequest,
                  onFinish: handleBackToSupporteds,
                }
              : undefined
          }
          listActions={
            created && !startedFromRequest
              ? {
                  onGoToRequest: created.canRequest && onContinueToRequest ? handleContinueToRequest : undefined,
                  onAddClassifications: onAddClassifications ? handleAddClassifications : undefined,
                }
              : undefined
          } />
      ) : isStartSteps || draftValues ? (
        <NewSupportedForm
          onSuccess={(_createdId, createdInfo) => {
            /*
             * The draft is discarded only here, after the record it was meant to become
             * exists. Discarding on open would lose everything typed in front of the
             * supported if the coordinator closed the form without finishing.
             */
            if (draft) onDraftCompleted?.(draft);
            /* canRequest rides along: only a supported that can be requested for right now is offered the request. */
            setCreated(createdInfo ?? null);
            setIsSuccess(true);
          }}
          onError={(errorMessage: string) => alert(errorMessage)}
          initialIsraeliId={initialIsraeliId}
          existingSupported={existingSupported}
          initialValues={draftValues}
        />
      ) : (
        <CheckExistence
          start={handleStart}
          openSupportedAdditionalInfo={onOpenSupportedAdditionalInfo} />
      )}
    </MainDialogLayout>
  );
};

export default NewSupported;
